import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth, ADMIN_ROLES } from '../../contexts/AuthContext';
import { useSystemSettings } from '../../hooks/useSystemSettings';
import { logAudit } from '../../lib/audit';

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, profile, role, loading, roleLoading, blockedReason } = useAuth();
  const { settings, loading: settingsLoading } = useSystemSettings();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const platformName  = settings.platform_name  || '';
  const platformSlogan = settings.platform_slogan || '';
  // ── Logo: NEVER falls back to a hardcoded URL — shows nothing until settings load ──
  const logoUrl = settings.logo_url || settings.logo_small_url || '';

  // ── Landing page dynamic texts ──
  const heroTitle     = settings.landing_hero_title     || 'Controle total das suas';
  const heroHighlight = settings.landing_hero_highlight || 'finanças pessoais';
  const features = [
    { icon: 'ri-pie-chart-2-line',  text: settings.landing_feature_1 || 'Visão completa das suas finanças' },
    { icon: 'ri-shield-check-line', text: settings.landing_feature_2 || 'Dados isolados e seguros por usuário' },
    { icon: 'ri-focus-3-line',      text: settings.landing_feature_3 || 'Metas financeiras inteligentes' },
    { icon: 'ri-bank-card-line',    text: settings.landing_feature_4 || 'Controle de cartões e faturas' },
  ];
  const stats = [
    { val: settings.landing_stat_1_val || '+12mil', label: settings.landing_stat_1_label || 'usuários ativos' },
    { val: settings.landing_stat_2_val || '4.9★',  label: settings.landing_stat_2_label || 'avaliação média' },
    { val: settings.landing_stat_3_val || '100%',  label: settings.landing_stat_3_label || 'dados seguros' },
  ];

  // Reset Google loading state when user returns to this page (e.g., pressed Back from Google OAuth)
  useEffect(() => {
    const resetGoogleLoading = () => setGoogleLoading(false);

    // Fired when tab becomes visible again (user switches back from Google tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetGoogleLoading();
      }
    };

    // Fired when page is restored from bfcache (browser back button)
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        resetGoogleLoading();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // Safety timeout: if googleLoading stays true for more than 60s, auto-reset
  useEffect(() => {
    if (!googleLoading) return;
    const timer = setTimeout(() => setGoogleLoading(false), 60_000);
    return () => clearTimeout(timer);
  }, [googleLoading]);

  // Show blocked/inactive message whenever AuthContext detects it
  useEffect(() => {
    if (blockedReason === 'blocked') {
      setError('Sua conta foi bloqueada. Entre em contato com o suporte para mais informações.');
    } else if (blockedReason === 'inactive') {
      setError('Sua conta está desativada. Entre em contato com o suporte para reativá-la.');
    }
  }, [blockedReason]);

  useEffect(() => {
    // Wait until both auth loading flags are done
    if (loading || roleLoading) return;
    // No user — stay on login page
    if (!user) return;
    // Role not yet determined — stay and wait
    if (role === null) {
      console.log('[Home] user authenticated but role is null — waiting');
      return;
    }

    if (ADMIN_ROLES.includes(role)) {
      console.log('[Home] redirecting — admin →', '/admin');
      navigate('/admin', { replace: true });
      return;
    }

    // For customer role: check if wizard is complete.
    // Profile may still be loading (null) — wait for it before deciding.
    if (profile === null) {
      console.log('[Home] user authenticated, role=customer, waiting for profile...');
      return;
    }

    const destination = !profile.wizard_completed ? '/cadastro' : '/dashboard';
    console.log('[Home] redirecting — email:', user.email, '| role:', role, '| wizard_completed:', profile.wizard_completed, '| →', destination);
    navigate(destination, { replace: true });
  }, [user, profile, role, loading, roleLoading, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const { error: err } = await supabase.auth.signInWithPassword({ email, password });

    if (err) {
      if (err.message.includes('Invalid login credentials')) {
        setError('E-mail ou senha incorretos. Verifique e tente novamente.');
      } else if (err.message.includes('Email not confirmed')) {
        setError('Confirme seu e-mail antes de entrar. Verifique sua caixa de entrada.');
      } else {
        setError(err.message);
      }
      setSubmitting(false);
      return;
    }

    // Auth succeeded — immediately check account_status BEFORE redirect
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('account_status, is_active')
          .eq('id', authUser.id)
          .maybeSingle();

        if (profileData) {
          if (profileData.account_status === 'blocked') {
            await supabase.auth.signOut();
            setError('Sua conta foi bloqueada. Entre em contato com o suporte para mais informações.');
            setSubmitting(false);
            return;
          }
          if (profileData.account_status === 'inactive' || profileData.is_active === false) {
            await supabase.auth.signOut();
            setError('Sua conta está desativada. Entre em contato com o suporte para reativá-la.');
            setSubmitting(false);
            return;
          }
        }
        // Log successful login
        logAudit({ action: 'login', resource: 'auth', resource_id: authUser.id, details: { method: 'email' } });
      }
    } catch {
      // If profile check fails, proceed normally
    }

    setSubmitting(false);
    // Let useEffect + AuthContext handle the redirect
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError('');

    // Redirect to origin root — Supabase handles the auth callback and
    // the app's onAuthStateChange + useEffect takes care of routing.
    // This avoids needing /dashboard to be whitelisted separately.
    const redirectTo = window.location.origin;

    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: { access_type: 'offline', prompt: 'select_account' },
      },
    });
    if (err) {
      const msg = err.message.toLowerCase();
      if (msg.includes('provider is not enabled') || msg.includes('unsupported provider')) {
        setError('Login com Google não está ativo neste momento. Use e-mail e senha ou fale com o suporte.');
      } else if (msg.includes('popup closed') || msg.includes('cancelled')) {
        setError('Login cancelado. Tente novamente.');
      } else {
        setError('Não foi possível conectar com o Google. Tente novamente.');
      }
      setGoogleLoading(false);
    }
    // If no error: browser redirects to Google — loading state stays true intentionally
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    const { error: err } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: `${window.location.origin}/perfil`,
    });
    if (!err) setForgotSent(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <i className="ri-loader-4-line text-3xl text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Maintenance banner — shown at top when maintenance mode is on */}
      {settings.maintenance_mode === 'true' && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white text-center text-sm font-600 py-2.5 px-4 flex items-center justify-center gap-2">
          <i className="ri-tools-line" />
          {settings.maintenance_message || 'Sistema em manutenção. Voltamos em breve.'}
        </div>
      )}

      {/* Left side */}
      <div className={`hidden lg:flex w-[55%] bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex-col justify-between p-12 relative overflow-hidden ${settings.maintenance_mode === 'true' ? 'pt-[60px]' : ''}`}>
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-100/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-100/30 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" />

        {/* Left side logo */}
        <div className="flex items-center gap-3 relative z-10">
          {settingsLoading ? (
            <div className="w-11 h-11 rounded-xl bg-emerald-100 animate-pulse shrink-0" />
          ) : logoUrl ? (
            <img src={logoUrl} alt={platformName} className="w-11 h-11 rounded-xl object-cover shrink-0" />
          ) : (
            <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <i className="ri-leaf-line text-emerald-500 text-xl" />
            </div>
          )}
          {platformName && <span className="text-xl font-700 text-gray-900">{platformName}</span>}
        </div>

        <div className="relative z-10 space-y-8">
          <div>
            <h1 className="text-4xl font-800 text-gray-900 leading-tight mb-4">
              {heroTitle}<br />
              suas <span className="text-emerald-500">{heroHighlight}</span>
            </h1>
            <p className="text-gray-600 text-lg font-400 leading-relaxed max-w-md">
              {platformSlogan}
            </p>
          </div>

          <div className="space-y-4">
            {features.map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-xl shrink-0">
                  <i className={`${f.icon} text-emerald-600 text-base`} />
                </div>
                <span className="text-gray-700 font-500 text-[15px]">{f.text}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-8">
            {stats.map((s, i) => (
              <div key={i}>
                <p className="text-2xl font-700 text-gray-900">{s.val}</p>
                <p className="text-sm text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2.5 h-2.5 rounded-full bg-red-300" />
              <div className="w-2.5 h-2.5 rounded-full bg-amber-300" />
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
              <span className="text-xs text-gray-400 ml-2 font-500">Dashboard · {platformName}</span>
            </div>
            <img
              src="https://readdy.ai/api/search-image?query=modern%20personal%20finance%20dashboard%20screenshot%20clean%20white%20UI%20with%20green%20charts%20and%20cards%20showing%20balance%20income%20expenses%20minimalist%20SaaS%20application%20interface&width=560&height=280&seq=finance_dash_preview&orientation=landscape"
              alt="Preview do Dashboard"
              className="w-full rounded-xl object-top object-cover h-40"
            />
          </div>
        </div>
      </div>

      {/* Right side */}
      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo — also without hardcoded fallback */}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden justify-center">
            {settingsLoading ? (
              <div className="w-10 h-10 rounded-xl bg-emerald-100 animate-pulse shrink-0" />
            ) : logoUrl ? (
              <img src={logoUrl} alt={platformName} className="w-10 h-10 rounded-xl object-cover shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                <i className="ri-leaf-line text-emerald-500 text-lg" />
              </div>
            )}
            {platformName && <span className="text-xl font-700 text-gray-900">{platformName}</span>}
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-700 text-gray-900 mb-2">Bem-vindo de volta</h2>
            <p className="text-gray-500 text-[15px]">Entre na sua conta para continuar</p>
          </div>

          {error && (
            <div className="mb-4 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5">
              <i className="ri-error-warning-line text-red-500 text-base mt-0.5 shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            className="w-full h-12 border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 font-600 rounded-xl transition-all text-[15px] cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-3 mb-5"
          >
            {googleLoading ? (
              <><i className="ri-loader-4-line animate-spin text-base" />Conectando...</>
            ) : (
              <>
                <img
                  src="https://readdy.ai/api/search-image?query=Google%20G%20logo%20colorful%20red%20blue%20yellow%20green%20simple%20icon%20flat%20design%20transparent%20background&width=20&height=20&seq=google_logo_icon&orientation=squarish"
                  alt="Google"
                  className="w-5 h-5 object-contain"
                />
                Continuar com Google
              </>
            )}
          </button>

          <div className="mb-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 font-500">ou entre com e-mail</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-500 text-gray-700 mb-1.5">E-mail</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="seu@email.com"
                  className="w-full pl-10 pr-4 h-12 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400 placeholder:text-gray-400 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-500 text-gray-700 mb-1.5">Senha</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full pl-10 pr-11 h-12 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400 placeholder:text-gray-400 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`} />
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button type="button" onClick={() => setShowForgot(true)} className="text-[13px] text-emerald-600 hover:text-emerald-700 font-500 cursor-pointer">
                Esqueceu a senha?
              </button>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl transition-colors text-[15px] cursor-pointer whitespace-nowrap disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <><i className="ri-loader-4-line animate-spin text-base" />Entrando...</>
              ) : (
                <>Entrar<i className="ri-arrow-right-line text-base" /></>
              )}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 font-500">ou</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <button
            onClick={() => navigate('/cadastro')}
            className="w-full h-12 border-2 border-emerald-200 text-emerald-600 font-600 rounded-xl hover:bg-emerald-50 transition-colors text-[15px] cursor-pointer whitespace-nowrap"
          >
            Criar conta grátis
          </button>

          <p className="text-center text-[12px] text-gray-400 mt-6 leading-relaxed">
            Ao entrar, você concorda com nossos{' '}
            {settings.terms_url ? (
              <a href={settings.terms_url} target="_blank" rel="noopener noreferrer nofollow" className="text-emerald-600 hover:underline">Termos de Uso</a>
            ) : (
              <span className="text-emerald-600">Termos de Uso</span>
            )}{' '}
            e{' '}
            {settings.privacy_policy_url ? (
              <a href={settings.privacy_policy_url} target="_blank" rel="noopener noreferrer nofollow" className="text-emerald-600 hover:underline">Política de Privacidade</a>
            ) : (
              <span className="text-emerald-600">Política de Privacidade</span>
            )}
          </p>

          {/* Help link */}
          {settings.help_url && (
            <div className="text-center mt-3">
              <a href={settings.help_url} target="_blank" rel="noopener noreferrer nofollow" className="text-[12px] text-gray-400 hover:text-emerald-600 transition-colors inline-flex items-center gap-1">
                <i className="ri-question-line text-sm" />Central de Ajuda
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Forgot password modal */}
      {showForgot && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); }}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            {forgotSent ? (
              <div className="text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                  <i className="ri-mail-check-line text-2xl text-emerald-600" />
                </div>
                <h3 className="text-lg font-700 text-gray-900 mb-2">E-mail enviado!</h3>
                <p className="text-sm text-gray-500 mb-5">Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.</p>
                <button onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); }} className="w-full h-11 bg-emerald-500 text-white font-600 rounded-xl cursor-pointer whitespace-nowrap text-sm">
                  Fechar
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-700 text-gray-900">Recuperar senha</h3>
                  <button onClick={() => setShowForgot(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer">
                    <i className="ri-close-line text-lg" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">Informe seu e-mail e enviaremos um link para redefinir sua senha.</p>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    placeholder="seu@email.com"
                    className="w-full h-11 px-4 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400 transition-all"
                  />
                  <button type="submit" className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl cursor-pointer whitespace-nowrap text-sm transition-colors">
                    Enviar link
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
